/**
 * Ad-unit benchmarks API client — the Ad Ops "Ad Benchmarks" and
 * "SOV/Takeover Benchmarks" tabs. Backend: /api/ad-ops/benchmarks (requires
 * adOps.campaignReports.manage).
 *
 * These values render on every client campaign report page (web + XLSX);
 * saves go live on the next page view — no report regeneration involved.
 * A monthly GAM refresh (or "Recompute now") replaces the numbers on rows
 * with enough data; `computed` describes the last one.
 */

import { apiRequest } from './api';

const BASE = '/api/ad-ops/benchmarks';

export type AdUnitBenchmarkKey =
  | 'ns-15s-pre-mid'
  | 'ctv-30s'
  | 'skippable-30s'
  | 'bumper-6s'
  | 'sov-takeover';

/** The four ad-format rows (everything but the blended takeover row). */
export type AdUnitFormatKey = Exclude<AdUnitBenchmarkKey, 'sov-takeover'>;

/** Which tab: standard ("Ad Benchmarks") or sponsorship ("SOV/Takeover Benchmarks"). */
export type BenchmarkSetId = 'standard' | 'sov';

/** Display strings shown verbatim on client reports ("0.03%", "N/A"). */
export interface AdUnitBenchmarkValues {
  label: string;
  ctr: string;
  ctrRange: string;
  vcr: string;
  vcrRange: string;
  /** Client-facing footnote (e.g. the CTV beta caveat). */
  note?: string;
}

/** The 3 full calendar months (LA) a GAM refresh computed from. */
export interface BenchmarkWindow {
  startYmd: string;
  endYmd: string;
  months: string[];
  /** e.g. '2026-07..2026-09' */
  key: string;
}

/** One metric's typical-campaign statistics. Rates are fractions (0.0021 = 0.21%). */
export interface BenchmarkMetricStats {
  /** Campaigns with any delivery in the row. */
  campaigns: number;
  /** Campaigns big enough to count (10,000+ impressions). */
  eligible: number;
  sufficient: boolean;
  median: number | null;
  p25: number | null;
  p75: number | null;
  /** All eligible campaigns pooled (Σevents ÷ Σimpressions) — context only. */
  pooled: number | null;
  impressions: number;
  events: number;
}

export interface ComputedBenchmarkValues {
  ctr: string;
  ctrRange: string;
  vcr: string;
  vcrRange: string;
}

export interface ComputedBenchmarkRow {
  /** null = not enough data; the row kept its values. */
  values: ComputedBenchmarkValues | null;
  /** null for CTV (no click benchmark). */
  ctr: BenchmarkMetricStats | null;
  vcr: BenchmarkMetricStats;
  orders: number;
  lineItems: number;
  impressions: number;
}

/** The last GAM computation for a set. */
export interface ComputedBenchmarkBlock {
  runId: string;
  computedAt: string;
  methodologyVersion: number;
  window: BenchmarkWindow;
  rows: Partial<Record<AdUnitBenchmarkKey, ComputedBenchmarkRow>>;
}

export interface AdBenchmarksState {
  values: Record<AdUnitFormatKey, AdUnitBenchmarkValues>;
  defaults: Record<AdUnitFormatKey, AdUnitBenchmarkValues>;
  order: AdUnitFormatKey[];
  computed: ComputedBenchmarkBlock | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface SovBenchmarksState {
  /** Resolved rows — placeholders already show their mirrored values. */
  values: Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>;
  /** "Reset to placeholder" target per format row (live standard numbers, SOV label). */
  mirror: Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>;
  /** Format rows with no numbers of their own yet. */
  placeholders: AdUnitFormatKey[];
  defaults: Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>;
  order: AdUnitBenchmarkKey[];
  computed: ComputedBenchmarkBlock | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** One displayed value a refresh changed. */
export interface BenchmarkChange {
  set: BenchmarkSetId;
  key: AdUnitBenchmarkKey;
  field: 'ctr' | 'ctrRange' | 'vcr' | 'vcrRange';
  from: string;
  to: string;
}

export interface AdBenchmarkRun {
  id: string;
  status: 'running' | 'completed' | 'failed';
  trigger: 'scheduled' | 'manual';
  requestedBy: string;
  window: BenchmarkWindow;
  startedAt: string;
  completedAt?: string;
  /** Why it failed — nothing is published by a failed run. */
  error?: string;
  totalImpressions?: number;
  changes?: BenchmarkChange[];
}

/** A GAM order kept out of every benchmark (a test or house order). */
export interface ExcludedBenchmarkOrder {
  orderId: string;
  /** The GAM order name or the reason given ('' when neither is known). */
  label: string;
  addedBy: string;
  addedAt: string;
}

export interface AdBenchmarksRefreshStatus {
  state: 'idle' | 'running' | 'failed';
  /** false = the monthly run is paused (Recompute now still works). */
  enabled: boolean;
  /** The window a run started now would compute. */
  window: BenchmarkWindow;
  /** YYYY-MM-DD; null while paused or before the first publish. */
  nextScheduledYmd: string | null;
  /** Nothing published from GAM yet — the monthly run waits for a first Recompute now. */
  awaitingFirstPublish: boolean;
  lastRun: AdBenchmarkRun | null;
  lastSuccess: { runId: string; windowKey: string; at: string; totalImpressions: number } | null;
  /** Orders the next refresh leaves out. */
  excludedOrders: ExcludedBenchmarkOrder[];
}

export const getAdBenchmarks = async (): Promise<AdBenchmarksState> => apiRequest(BASE);

/** Full record. `expectedUpdatedAt` = the loaded state's updatedAt (409 if someone saved since). */
export const saveAdBenchmarks = async (
  values: Record<AdUnitFormatKey, AdUnitBenchmarkValues>,
  expectedUpdatedAt: string | null,
): Promise<AdBenchmarksState> =>
  apiRequest(BASE, { method: 'PUT', body: JSON.stringify({ values, expectedUpdatedAt }) });

export const getSovBenchmarks = async (): Promise<SovBenchmarksState> => apiRequest(`${BASE}/sov`);

/** `values` = the rows with their own numbers; a row left out is (or becomes) a placeholder. */
export const saveSovBenchmarks = async (
  values: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>>,
  expectedUpdatedAt: string | null,
): Promise<SovBenchmarksState> =>
  apiRequest(`${BASE}/sov`, { method: 'PUT', body: JSON.stringify({ values, expectedUpdatedAt }) });

export const getAdBenchmarksRefreshStatus = async (): Promise<AdBenchmarksRefreshStatus> => apiRequest(`${BASE}/refresh`);

/**
 * "Recompute now" — recomputes BOTH sets from GAM in the background (a few
 * minutes). 409 with code 'already_running' while a run is going.
 */
export const startAdBenchmarksRefresh = async (): Promise<{ runId: string; window: BenchmarkWindow }> =>
  apiRequest(`${BASE}/refresh`, { method: 'POST' });

/**
 * Replace the excluded-orders list (applies from the next refresh). An order
 * saved without a label is named from GAM.
 */
export const saveExcludedBenchmarkOrders = async (
  excludedOrders: { orderId: string; label?: string }[],
): Promise<{ excludedOrders: ExcludedBenchmarkOrder[] }> =>
  apiRequest(`${BASE}/refresh/excluded-orders`, { method: 'PUT', body: JSON.stringify({ excludedOrders }) });
